import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { approveChangeOrderByToken } from '@/lib/api/changeOrders';

type State = 'form' | 'submitting' | 'approved' | 'declined' | 'already_approved' | 'error';

// Full CO detail so the client sees exactly what they're authorizing (audit:
// this page previously asked for approval with no title, no items, no amount)
interface CoItem { description: string; quantity: number; unit_price: number; total: number }
interface CoDetail {
  change_order_number: string; title: string | null; description: string | null;
  status: string; subtotal: number; tax_rate: number; tax_amount: number; total: number;
  job_title: string | null; items: CoItem[];
}

const fmt = (n: unknown) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ChangeOrderApprovePage = () => {
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState('');
  const [state, setState] = useState<State>('form');
  const [errorMsg, setErrorMsg] = useState('');
  const [co, setCo] = useState<CoDetail | null>(null);
  const [coLoading, setCoLoading] = useState(true);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  useEffect(() => {
    if (!token) { setCoLoading(false); return; }
    fetch(`/api/change-orders/approve/${token}`)
      .then(r => r.json())
      .then(j => {
        if (j?.data) {
          setCo(j.data);
          if (j.data.status === 'approved') setState('already_approved');
          if (j.data.status === 'declined') setState('declined');
        } else {
          setErrorMsg(j?.error || 'This link is invalid or has expired.');
          setState('error');
        }
      })
      .catch(() => { setErrorMsg('Could not load the change order — check your connection.'); setState('error'); })
      .finally(() => setCoLoading(false));
  }, [token]);

  const handleDecline = async () => {
    if (!token) return;
    try {
      setState('submitting');
      await fetch(`/api/change-orders/decline/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Client', reason: declineReason.trim() || undefined }),
      }).then(r => { if (!r.ok) throw new Error(); });
      setState('declined');
    } catch {
      setErrorMsg('Could not record your response — please call us at (250) 510-0677.');
      setState('error');
    }
  };

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      setState('submitting');
      await approveChangeOrderByToken(token, name.trim() || 'Client');
      setState('approved');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || '';
      if (msg.toLowerCase().includes('already')) {
        setState('already_approved');
      } else {
        setErrorMsg(msg || 'Something went wrong. Please contact OchrePoint Contracting.');
        setState('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="material-symbols-outlined text-[#C77D3F] text-4xl">construction</span>
          <div>
            <h1 className="text-2xl font-black text-gray-900 leading-tight">OchrePoint</h1>
            <p className="text-xs text-gray-500 tracking-wide">CONTRACTING</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {state === 'form' || state === 'submitting' ? (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center size-12 rounded-full bg-primary/10">
                  <span className="material-symbols-outlined text-primary text-2xl">edit_note</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Change Order Approval</h2>
                  <p className="text-sm text-gray-500">Review and approve the proposed change</p>
                </div>
              </div>

              {coLoading ? (
                <div className="h-32 rounded-xl bg-gray-100 animate-pulse mb-6" />
              ) : co && (
                <div className="mb-6 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-[#FAF6F0] border-b border-gray-200">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#C77D3F]">{co.change_order_number}{co.job_title ? ` · ${co.job_title}` : ''}</p>
                    {co.title && <p className="font-semibold text-gray-900 text-sm mt-0.5">{co.title}</p>}
                    {co.description && <p className="text-xs text-gray-500 mt-1">{co.description}</p>}
                  </div>
                  {co.items.length > 0 && (
                    <ul className="divide-y divide-gray-50 px-4">
                      {co.items.map((it, i) => (
                        <li key={i} className="py-2 flex justify-between gap-3 text-sm">
                          <span className="text-gray-700 min-w-0">{it.description} <span className="text-gray-400 text-xs">× {Number(it.quantity)}</span></span>
                          <span className="font-semibold text-gray-900 whitespace-nowrap">{fmt(it.total)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="px-4 py-3 border-t border-gray-200 space-y-0.5 text-sm">
                    {Number(co.tax_amount) > 0 && (
                      <>
                        <div className="flex justify-between text-gray-500 text-xs"><span>Subtotal</span><span>{fmt(co.subtotal)}</span></div>
                        <div className="flex justify-between text-gray-500 text-xs"><span>Tax ({Number(co.tax_rate)}%)</span><span>{fmt(co.tax_amount)}</span></div>
                      </>
                    )}
                    <div className="flex justify-between font-black text-[#C77D3F]"><span>Change total</span><span>{fmt(co.total)}</span></div>
                  </div>
                </div>
              )}

              <form onSubmit={handleApprove} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Optional — helps us record who approved</p>
                </div>

                <button
                  type="submit"
                  disabled={state === 'submitting'}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-60"
                >
                  {state === 'submitting' ? (
                    <>
                      <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                      Approving…
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      Approve Change Order
                    </>
                  )}
                </button>
              </form>

              {!showDecline ? (
                <button type="button" onClick={() => setShowDecline(true)}
                  className="w-full mt-2 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-semibold">
                  Decline instead
                </button>
              ) : (
                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                  <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={2}
                    placeholder="What didn't work for you? (optional)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40 resize-none" />
                  <button type="button" onClick={handleDecline} disabled={state === 'submitting'}
                    className="w-full py-2.5 bg-gray-500 text-white rounded-xl text-sm font-bold disabled:opacity-60">
                    Confirm decline
                  </button>
                </div>
              )}

              <p className="text-xs text-gray-400 text-center mt-4">
                By clicking "Approve" you are electronically authorizing this change order{co ? ` for ${fmt(co.total)}` : ''}.
              </p>
            </>
          ) : state === 'approved' ? (
            <div className="text-center py-6 space-y-4">
              <div className="flex items-center justify-center">
                <div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Change Order Approved</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Thank you{name ? `, ${name}` : ''}. Your approval has been recorded and OchrePoint Contracting
                has been notified. Work will proceed as outlined in the change order.
              </p>
              <p className="text-xs text-gray-400 mt-2">You may close this window.</p>
            </div>
          ) : state === 'declined' ? (
            <div className="text-center py-6 space-y-4">
              <div className="flex items-center justify-center">
                <div className="size-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-gray-500 text-3xl">thumb_down</span>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Change Order Declined</h2>
              <p className="text-sm text-gray-600">No problem — we've been notified. We'll follow up to talk through the options.</p>
              <p className="text-xs text-gray-400 mt-2">You may close this window.</p>
            </div>
          ) : state === 'already_approved' ? (
            <div className="text-center py-6 space-y-4">
              <div className="flex items-center justify-center">
                <div className="size-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600 text-3xl">info</span>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Already Approved</h2>
              <p className="text-sm text-gray-600">This change order has already been approved.</p>
              <p className="text-xs text-gray-400 mt-2">You may close this window.</p>
            </div>
          ) : (
            <div className="text-center py-6 space-y-4">
              <div className="flex items-center justify-center">
                <div className="size-16 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-red-600 text-3xl">error</span>
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Approval Failed</h2>
              <p className="text-sm text-gray-600">{errorMsg}</p>
              <button
                onClick={() => setState('form')}
                className="text-sm text-primary underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          OchrePoint Contracting LTD · Duncan &amp; North Cowichan, BC
        </p>
      </div>
    </div>
  );
};
